import * as api from "../Services/api";
import School from "../../objs/School";
import Provider from "../../objs/Provider";
import Client from "../../objs/Client";
import Team from "../../objs/Team";
import Semester from "../../objs/Semester";
import Program from "../../objs/Program";
import Discipline from "../../objs/Discipline";
import DisciplineSubCode from "../../objs/DisciplineSubCode";
import Service from "../../objs/Service";
import DocumentType from "../../objs/DocumentType";
import DistrictAdmin from "../../objs/DistrictAdmin";
import Physician from "../../objs/Physician";
import PlanType from "../../objs/PlanType";
import SessionLocation from "../../objs/SessionLocation";

export const state = {
  genders: [],
  languages: [],
  disciplines: [],
  disciplineSubCodes: [],
  titles: [],
  caregiverRelationships: [],
  eiStaffingTeams: [],
  phoneTypes: [],
  usStates: [
    "AL",
    "AK",
    "AS",
    "AZ",
    "AR",
    "CA",
    "CO",
    "CT",
    "DE",
    "DC",
    "FM",
    "FL",
    "GA",
    "GU",
    "HI",
    "ID",
    "IL",
    "IN",
    "IA",
    "KS",
    "KY",
    "LA",
    "ME",
    "MH",
    "MD",
    "MA",
    "MI",
    "MN",
    "MS",
    "MO",
    "MT",
    "NE",
    "NV",
    "NH",
    "NJ",
    "NM",
    "NY",
    "NC",
    "ND",
    "MP",
    "OH",
    "OK",
    "OR",
    "PW",
    "PA",
    "PR",
    "RI",
    "SC",
    "SD",
    "TN",
    "TX",
    "UT",
    "VT",
    "VI",
    "VA",
    "WA",
    "WV",
    "WI",
    "WY",
  ],
  physicians: [],
  schools: [],
  teams: [],
  semesters: [],
  programs: [],
  services: [],
  providers: [],
  clients: [],
  supervisors: [],
  districtAdmins: [],
  documentTypes: [],
  planTypes: [],
  sessionLocations: [],
  caseNoteSubjects: [],
  caseNoteTags: [],
};
export const getters = {
  getFilteredSchoolsByIsSummerCamp: (state) => (isSummerCamp) => {
    return state.schools.filter((item) => item.summerCamp === isSummerCamp);
  },
  getSchoolById: (state) => (schoolId) => {
    return state.schools.find((item) => item.schoolId == schoolId);
  },
  getSemesterById: (state) => (semesterId) => {
    return state.semesters.find((item) => item.semesterId == semesterId);
  },
  getSemesterByDate: (state) => (date) => {
    return state.semesters.find(
      (item) =>
        new Date(date) >= new Date(item.semesterStartDate) &&
        new Date(date) <= new Date(item.semesterEndDate)
    );
  },
  getCurrentSemester: (state, getters) => {
    return getters.getSemesterByDate(new Date());
  },
  getServiceId: (state) => (serviceCode) => {
    return state.services.find((item) => item.code === serviceCode)?.serviceId;
  },
  getProgramCode: (state) => (programId) => {
    return state.programs.find((item) => item.programId === programId)?.code;
  },
  getProviderById: (state) => (providerId) => {
    return state.providers.find((item) => item.providerId === providerId);
  },
  getProviderExists: (state) => (providerId) => {
    return state.providers.map((item) => item.providerId).includes(providerId);
  },
  getDistrictAdminById: (state) => (districtAdminId) => {
    return state.districtAdmins.find(
      (item) => item.districtAdminId === districtAdminId
    );
  },
  getAgeCategoryByPlanType: (state) => (planTypeId) => {
    return state.planTypes.find((item) => item.planTypeId === planTypeId)
      .ageCategory;
  },
  getDisciplinesForService: (state) => (serviceId) => {
    return state.disciplines.filter((disc) =>
      disc.services.some((service) => service.serviceId == serviceId)
    );
  },
  getServiceIdsForProgram: (state) => (programId) => {
    return new Set(
      state.programs
        .find((x) => x.programId == programId)
        ?.services.map((s) => s.serviceId)
    );
  },
};

export const mutations = {
  SET_OPTIONS(state, payload) {
    state.genders = payload.genders;
    state.languages = payload.languages;
    state.disciplines = payload.disciplines.map((item) => new Discipline(item));
    state.disciplineSubCodes = payload.disciplineSubCodes.map(
      (item) => new DisciplineSubCode(item)
    );
    state.caregiverRelationships = payload.caregiverRelationships;
    state.eiStaffingTeams = payload.eiStaffingTeams;
    state.phoneTypes = payload.phoneTypes;
    state.titles = payload.titles;
    state.physicians = payload.physicians.map((item) => new Physician(item));
    state.schools = payload.schools
      .map((item) => new School(item))
      .sort((a, b) => a.schoolName.localeCompare(b.schoolName));
    state.teams = payload.teams.map((item) => new Team(item));
    state.semesters = payload.semesters.map((item) => new Semester(item));
    state.programs = payload.programs.map((item) => new Program(item));
    state.services = payload.services.map((item) => new Service(item));
    state.supervisors = payload.supervisors.map((item) => new Provider(item));
    state.districtAdmins = payload.districtAdmins
      .map((item) => new DistrictAdmin(item))
      .sort((a, b) => a.firstName.localeCompare(b.firstName));
    state.documentTypes = payload.documentTypes
      .map((item) => new DocumentType(item))
      .sort((a, b) => a.name.localeCompare(b.name));
    state.planTypes = payload.planTypes
      .map((item) => new PlanType(item))
      .sort((a, b) => a.ageCategory.localeCompare(b.ageCategory));
    state.sessionLocations = payload.sessionLocations.map(
      (item) => new SessionLocation(item)
    );
    state.caseNoteSubjects = payload.caseNoteSubjects;
    state.caseNoteTags = payload.caseNoteTags;
  },
  SET_PROVIDERS(state, payload) {
    state.providers = payload
      .map((item) => new Provider(item))
      .sort((a, b) => a.lastName.localeCompare(b.lastName));
  },
  ADD_PROVIDER(state, payload) {
    state.providers.push(new Provider(payload));
  },
  UPDATE_PROVIDER(state, payload) {
    const findIndex = state.providers.findIndex(
      ({ providerId }) => providerId === payload.providerId
    );
    state.providers.splice(findIndex, 1, { ...payload });
  },
  ADD_PHYSICIAN(state, payload) {
    state.physicians.push(new Physician(payload));
  },
  UPDATE_PHYSICIAN(state, payload) {
    const findIndex = state.physicians.findIndex(
      ({ physicianId }) => physicianId === payload.physicianId
    );
    state.physicians.splice(findIndex, 1, { ...payload });
  },
  SET_CLIENTS(state, payload) {
    state.clients = payload
      .map((item) => new Client(item))
      .sort((a, b) => a.lastName.localeCompare(b.lastName));
  },
  ADD_SCHOOL(state, payload) {
    state.schools.push(new School(payload));
  },
  UPDATE_SCHOOL(state, payload) {
    const findIndex = state.schools.findIndex(
      ({ schoolId }) => schoolId === payload.schoolId
    );
    state.schools.splice(findIndex, 1, { ...payload });
  },
  ADD_DISTRICT_ADMIN(state, payload) {
    state.districtAdmins.push(new DistrictAdmin(payload));
  },
  UPDATE_DISTRICT_ADMIN(state, payload) {
    const findIndex = state.districtAdmins.findIndex(
      ({ districtAdminId }) => districtAdminId === payload.districtAdminId
    );
    state.districtAdmins.splice(findIndex, 1, { ...payload });
  },
};
export const actions = {
  async getClientOptions({ commit }) {
    return await api.get("/options/getClientOptions").then((response) => {
      if (response.success) {
        commit("SET_OPTIONS", response.data);
      }
    });
  },
  async getAllProviders({ commit }) {
    return await api.get("/provider/getAllProviders").then((response) => {
      if (response.success) {
        commit("SET_PROVIDERS", response.data);
      }
    });
  },
  async importSingleProvider({ commit, state, getters }, data) {
    var response = await api.postQuery(
      "/bOEImport/importSingleProvider/",
      data
    );
    if (response.success) {
      if (getters.getProviderExists(response.data?.providerId)) {
        commit("UPDATE_PROVIDER", response.data);
      } else {
        commit("ADD_PROVIDER", response.data);
      }
      return response.data?.providerId;
    } else {
      return null;
    }
  },
  async getAllClients({ commit }) {
    return await api.get("/options/getAllClients").then((response) => {
      if (response.success) {
        commit("SET_CLIENTS", response.data);
      }
    });
  },
  async createSchool({ commit }, data) {
    return await api.post("/school/createSchool", data).then((response) => {
      if (response.success) {
        commit("ADD_SCHOOL", response.data);
        return {
          schoolId: response.data.schoolId,
          message: "",
        };
      }
      return { schoolId: null, message: response.message };
    });
  },
  async updateSchool({ commit }, data) {
    return await api.post("/school/updateSchool", data).then((response) => {
      if (response.success) {
        commit("UPDATE_SCHOOL", response.data);
        return {
          schoolId: response.data.schoolId,
          message: "",
        };
      }
      return { schoolId: null, message: response.message };
    });
  },
  async createDistrictAdmin({ commit }, data) {
    return await api
      .post("/provider/createDistrictAdmin", data)
      .then((response) => {
        if (response.success) {
          commit("ADD_DISTRICT_ADMIN", response.data);
          return "";
        }
        return response.message;
      });
  },
  async updateDistrictAdmin({ commit }, data) {
    return await api
      .post("/provider/updateDistrictAdmin", data)
      .then((response) => {
        if (response.success) {
          commit("UPDATE_DISTRICT_ADMIN", response.data);
          return "";
        }
        return response.message;
      });
  },
  async createPhysician({ commit }, data) {
    return await api
      .post("/physician/createPhysician", data)
      .then((response) => {
        if (response.success) {
          commit("ADD_PHYSICIAN", response.data);
          return "";
        }
        return response.message;
      });
  },
  async updatePhysician({ commit }, data) {
    return await api
      .post("/physician/updatePhysician", data)
      .then((response) => {
        if (response.success) {
          commit("UPDATE_PHYSICIAN", response.data);
          return "";
        }
        return response.message;
      });
  },
  async createProvider({ commit }, data) {
    return await api.post("/provider/createProvider", data).then((response) => {
      if (response.success) {
        commit("ADD_PROVIDER", response.data);
        return {
          providerId: response.data.providerId,
          message: "",
        };
      }
      return { providerId: null, message: response.message };
    });
  },
  async updateProvider({ commit }, data) {
    return await api.post("/provider/updateProvider", data).then((response) => {
      if (response.success) {
        commit("UPDATE_PROVIDER", response.data);
        return {
          providerId: response.data.providerId,
          message: "",
        };
      }
      return { providerId: null, message: response.message };
    });
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
